import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { findHelpTarget, HelpHighlightContext, type HelpHighlightApi } from './useHelpHighlight';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Ring padding around the element, px. */
const PAD = 3;
/** One pulse ring, ms (two rings with a gap, then clear). */
const PULSE_MS = 300;

/**
 * App-root highlight overlay for living help (ADR-0066). Help chips, field rows and tour steps
 * name a control by its `data-help-id`; this draws ONE fixed, click-through ring over the first
 * matching element. It knows nothing about help content.
 *
 * <p>The ring is portalled to `body` and positioned from `getBoundingClientRect()`, re-measured
 * in a requestAnimationFrame on every scroll (capture phase, so scrolling inside a grid or the
 * main pane counts) and resize while active. Nothing is added to the target itself, so
 * AG-Grid's own DOM and `data-testid`s are untouched. z-50 puts it above the panel (z-40).
 */
export function HelpHighlightProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [ringOn, setRingOn] = useState(true);
  const timers = useRef<number[]>([]);

  const cancelTimers = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  }, []);

  const clear = useCallback(() => {
    cancelTimers();
    setTarget(null);
    setBox(null);
  }, [cancelTimers]);

  const show = useCallback((id: string): boolean => {
    const el = findHelpTarget(id);
    if (!el) return false;
    // jsdom has no scrollIntoView; real browsers always do.
    el.scrollIntoView?.({ block: 'nearest' });
    setRingOn(true);
    setTarget(el);
    return true;
  }, []);

  const highlight = useCallback(
    (id: string) => {
      cancelTimers();
      return show(id);
    },
    [cancelTimers, show],
  );

  const pulse = useCallback(
    (id: string) => {
      cancelTimers();
      if (!show(id)) return false;
      const at = (ms: number, fn: () => void) => timers.current.push(window.setTimeout(fn, ms));
      at(PULSE_MS, () => setRingOn(false));
      at(PULSE_MS + PULSE_MS / 2, () => setRingOn(true));
      at(PULSE_MS * 2 + PULSE_MS / 2, () => {
        setTarget(null);
        setBox(null);
      });
      return true;
    },
    [cancelTimers, show],
  );

  const isMounted = useCallback((id: string) => findHelpTarget(id) != null, []);

  // Measure while active; follow scroll/resize via rAF.
  useEffect(() => {
    if (!target) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      if (!target.isConnected) {
        setTarget(null);
        setBox(null);
        return;
      }
      const r = target.getBoundingClientRect();
      setBox({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };
    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [target]);

  useEffect(() => cancelTimers, [cancelTimers]);

  const api = useMemo<HelpHighlightApi>(
    () => ({ highlight, pulse, clear, isMounted }),
    [highlight, pulse, clear, isMounted],
  );

  return (
    <HelpHighlightContext.Provider value={api}>
      {children}
      {target && box && ringOn
        ? createPortal(
            <div
              aria-hidden="true"
              data-testid="help-highlight-overlay"
              className="pointer-events-none fixed z-50 rounded outline outline-2 outline-phblue-500 ring-4 ring-phblue-300/40 transition-[top,left,width,height] duration-75"
              style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
            />,
            document.body,
          )
        : null}
    </HelpHighlightContext.Provider>
  );
}
